;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import type { PropertyStaffRoleFilter } from "@/lib/services/property/property-staff-role";
import type { ProviderType } from "@/types/enums";
import type { PropertyVisibilityFilter } from "@/types/property";

type WorkspacePropertiesKeyParams = {
	includeInactive?: boolean;
	shouldFilterByPropertyGroupIds: boolean;
	visibility?: PropertyVisibilityFilter | string | null;
	workspaceId: string;
};

type WorkspacePropertyStaffKeyParams = {
	providerType?: ProviderType;
	roleFilter?: PropertyStaffRoleFilter | PropertyStaffRoleFilter[];
	shouldFilterByPropertyGroupIds?: boolean;
	workspaceId: string;
};

type WorkspaceFilterOptionsKeyParams = {
	shouldFilterByPropertyGroupIds: boolean;
	visibility: string;
	workspaceId: string;
};

type WorkspaceAutoSyncKeyParams = {
	userId?: string;
	workspaceId: string;
};

type WorkspaceCommunicationsInquiryReadinessKeyParams = {
	selectedCommunicationSource?: string | null;
	workspaceId: string;
};

export const workspaceQueryKeys = {
	all: ["workspace"] as const,
	appfolioCustomFieldCatalogs: (workspaceId: string) =>
		[
			...workspaceQueryKeys.detail(workspaceId),
			"appfolio-custom-field-catalogs",
		] as const,
	autoSync: ({ userId, workspaceId }: WorkspaceAutoSyncKeyParams) =>
		["user_workspace_auto_sync", workspaceId, userId] as const,
	chartOfAccounts: (workspaceId: string) =>
		[...workspaceQueryKeys.detail(workspaceId), "chart-of-accounts"] as const,
	chartOfAccountsAvailability: (workspaceId: string) =>
		[
			...workspaceQueryKeys.chartOfAccounts(workspaceId),
			"availability",
		] as const,
	communicationsInquiryReadiness: ({
		selectedCommunicationSource,
		workspaceId,
	}: WorkspaceCommunicationsInquiryReadinessKeyParams) =>
		[
			...workspaceQueryKeys.detail(workspaceId),
			"communications-inquiry-readiness",
			selectedCommunicationSource,
		] as const,
	detail: (workspaceId: string) =>
		[...workspaceQueryKeys.all, workspaceId] as const,
	filterOptions: ({
		shouldFilterByPropertyGroupIds,
		visibility,
		workspaceId,
	}: WorkspaceFilterOptionsKeyParams) =>
		[
			...workspaceQueryKeys.detail(workspaceId),
			"filter-options",
			{ shouldFilterByPropertyGroupIds, visibility },
		] as const,
	glAccountMapping: (workspaceId: string) =>
		[...workspaceQueryKeys.detail(workspaceId), "gl-account-mapping"] as const,
	invitations: (workspaceId: string) =>
		[...workspaceQueryKeys.detail(workspaceId), "invitations"] as const,
	lists: () => ["workspaces"] as const,
	properties: ({
		includeInactive,
		shouldFilterByPropertyGroupIds,
		visibility,
		workspaceId,
	}: WorkspacePropertiesKeyParams) =>
		includeInactive
			? ([
					...workspaceQueryKeys.propertiesPrefix(workspaceId),
					{
						includeInactive: true,
						shouldFilterByPropertyGroupIds,
						visibility,
					},
				] as const)
			: ([
					...workspaceQueryKeys.propertiesPrefix(workspaceId),
					{ shouldFilterByPropertyGroupIds, visibility },
				] as const),
	propertiesPrefix: (workspaceId: string) =>
		[...workspaceQueryKeys.detail(workspaceId), "properties"] as const,
	propertyGroups: ({
		shouldFilterByPropertyGroupIds,
		visibility,
		workspaceId,
	}: WorkspacePropertiesKeyParams) =>
		[
			...workspaceQueryKeys.propertyGroupsPrefix(workspaceId),
			{ shouldFilterByPropertyGroupIds, visibility },
		] as const,
	propertyGroupsPrefix: (workspaceId: string) =>
		[...workspaceQueryKeys.detail(workspaceId), "property-groups"] as const,
	propertyStaff: ({
		providerType,
		roleFilter,
		shouldFilterByPropertyGroupIds,
		workspaceId,
	}: WorkspacePropertyStaffKeyParams) =>
		[
			...workspaceQueryKeys.propertyStaffPrefix(workspaceId),
			{ providerType, roleFilter, shouldFilterByPropertyGroupIds },
		] as const,
	propertyStaffPrefix: (workspaceId: string) =>
		[...workspaceQueryKeys.detail(workspaceId), "property-staff"] as const,
	reputationProperties: (workspaceId: string) =>
		[
			...workspaceQueryKeys.detail(workspaceId),
			"reputation-properties",
		] as const,
	tenants: (workspaceId: string) =>
		[...workspaceQueryKeys.detail(workspaceId), "tenants"] as const,
	units: (workspaceId: string) =>
		[...workspaceQueryKeys.detail(workspaceId), "units"] as const,
	userCount: (workspaceId: string) =>
		[...workspaceQueryKeys.users(workspaceId), "count"] as const,
	users: (workspaceId: string) =>
		[...workspaceQueryKeys.detail(workspaceId), "users"] as const,
};

export const workspaceMutationKeys = {
	appfolioCustomFieldCatalogs: (workspaceId: string) =>
		workspaceQueryKeys.appfolioCustomFieldCatalogs(workspaceId),
	appfolioCustomFieldCatalogsRefresh: (workspaceId: string) =>
		[
			...workspaceQueryKeys.appfolioCustomFieldCatalogs(workspaceId),
			"refresh",
		] as const,
	chartOfAccountsResync: (workspaceId: string) =>
		["chart-of-accounts-resync", workspaceId] as const,
	create: ["workspace", "create"] as const,
	glAccountMapping: (workspaceId: string) =>
		workspaceQueryKeys.glAccountMapping(workspaceId),
	inviteUsers: (workspaceId: string) => ["invite-users", workspaceId] as const,
	propertiesResync: (workspaceId: string) =>
		["properties-resync", workspaceId] as const,
	propertyStaffResync: (workspaceId: string) =>
		["property-staff-resync", workspaceId] as const,
	resendUserInvitation: (workspaceId: string) =>
		["resend-user-invitation", workspaceId] as const,
	settingsAddDashboardView: (workspaceId: string) =>
		["settings", "workspace", workspaceId, "add_dashboard_view"] as const,
	settingsDeleteDashboardView: (workspaceId: string) =>
		["settings", "workspace", workspaceId, "delete_dashboard_view"] as const,
	settingsEdit: (workspaceId: string) =>
		["settings", "workspace", workspaceId, "edit"] as const,
	settingsEditDashboardView: (workspaceId: string) =>
		["settings", "workspace", workspaceId, "edit_dashboard_view"] as const,
	unitsResync: (workspaceId: string) => ["units-resync", workspaceId] as const,
	usersBulkDelete: ["workspace", "users", "bulk-delete"] as const,
	usersDelete: ["workspace", "users", "delete"] as const,
	usersEdit: ["workspace", "users", "edit"] as const,
	usersInvitationDelete: [
		"workspace",
		"users",
		"invitation",
		"delete",
	] as const,
};
